import { z } from "zod"
import { CAPABILITIES, roleSchema } from "./roles.js"
import { ENVIRONMENTS, dateRangeSchema } from "./filters.js"
import { SPLIT_KEYS, WIDGET_TYPES } from "./dashboard.js"
import { metricIdSchema } from "./metric-point.js"

export const TILE_STATES = ["ok", "empty", "unsupported", "stale"] as const
export type TileState = (typeof TILE_STATES)[number]

export const tileQueryRequestSchema = z.object({
  tileKey: z.string().optional(),
  metrics: z.array(metricIdSchema).min(1).max(8),
  /** Empty array = the session's whole tenantScope. A request may narrow it, never widen it */
  tenantIds: z.array(z.string()).default([]),
  environments: z.array(z.enum(ENVIRONMENTS)).default([]),
  includeChurned: z.boolean().default(false),
  range: dateRangeSchema,
  granularity: z.enum(["auto", "day", "week", "month"]).default("auto"),
  splitBy: z.array(z.enum(SPLIT_KEYS)).default(["period"]),
  topN: z.number().int().min(1).max(200).optional(),
  comparePrevious: z.boolean().default(false),
  refresh: z.boolean().default(false),
})
export type TileQueryRequest = z.infer<typeof tileQueryRequestSchema>

export const tileQueryBatchSchema = z.object({
  requests: z.array(tileQueryRequestSchema).min(1).max(24),
})
export type TileQueryBatch = z.infer<typeof tileQueryBatchSchema>

export const tileColumnSchema = z.object({
  key: z.string(),
  title: z.string(),
  role: z.enum(["measure", "dimension", "time"]),
  unit: z.enum(["count", "duration_sec", "ratio", "timestamp", "text"]).optional(),
  metricId: metricIdSchema.optional(),
})
export type TileColumn = z.infer<typeof tileColumnSchema>

export const tenantSliceSchema = z.object({
  tenantId: z.string(),
  displayName: z.string(),
  environment: z.enum(ENVIRONMENTS),
  /** From tenant_registry.color_slot, permanently bound to this customer. The frontend never uses an array index as a colour index */
  colorSlot: z.number().int().min(1).max(8),
  churned: z.boolean(),
  state: z.enum(TILE_STATES),
  stateDetail: z.string().optional(),
  lastCollectedAt: z.iso.datetime({ offset: true }).nullable(),
})
export type TenantSlice = z.infer<typeof tenantSliceSchema>

export const PREV_COLUMN_SUFFIX = "__prev"

export const tileResultSchema = z.object({
  state: z.enum(TILE_STATES),
  columns: z.array(tileColumnSchema),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
  perTenant: z.array(tenantSliceSchema),
  meta: z.object({
    metrics: z.array(
      z.object({
        id: metricIdSchema,
        name: z.string(),
        unit: z.string(),
        approximate: z.boolean(),
        caveat: z.string().optional(),
      }),
    ),
    resolvedRange: z.object({
      from: z.iso.datetime({ offset: true }),
      to: z.iso.datetime({ offset: true }),
      granularity: z.enum(["day", "week", "month", "all"]),
    }),
    previousRange: z
      .object({
        from: z.iso.datetime({ offset: true }),
        to: z.iso.datetime({ offset: true }),
      })
      .optional(),
    truncated: z.boolean(),
    generatedAt: z.iso.datetime({ offset: true }),
    fromCache: z.boolean(),
  }),
})
export type TileResult = z.infer<typeof tileResultSchema>

export const staffMeSchema = z.object({
  staffId: z.string(),
  account: z.string(),
  username: z.string(),
  role: roleSchema,
  tenantScope: z.array(z.string()),
  /*
   * Derived from the role by `capabilitiesOf`, never stored. It travels to the browser so the
   * interface can hide a control the server would refuse — the same table decides both, so the
   * two cannot disagree and say so only after the click.
   */
  capabilities: z.array(z.enum(CAPABILITIES)),
})
export type StaffMe = z.infer<typeof staffMeSchema>

export const staffSummarySchema = z.object({
  staffId: z.string(),
  account: z.string(),
  username: z.string(),
  role: roleSchema,
  tenantScope: z.array(z.string()),
  active: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
})
export type StaffSummary = z.infer<typeof staffSummarySchema>

export const createStaffSchema = z.object({
  account: z.email("Enter a valid email address").max(200),
  username: z.string().min(1, "Enter a name").max(120),
  role: roleSchema,
  /** `["*"]` = every customer. Narrowing it is how a person is limited to the accounts they handle */
  tenantScope: z.array(z.string()).min(1).default(["*"]),
})
export type CreateStaffRequest = z.infer<typeof createStaffSchema>

/*
 * The password is generated by the server and returned exactly once, at creation. There is no
 * mail path, so an administrator passes it on out of band; the person changes it once signed in.
 * Returning it on any later read would turn a list page into a credential dump.
 */
export const createdStaffSchema = staffSummarySchema.extend({ initialPassword: z.string() })
export type CreatedStaff = z.infer<typeof createdStaffSchema>

export const updateStaffSchema = z
  .object({
    username: z.string().min(1).max(120).optional(),
    role: roleSchema.optional(),
    tenantScope: z.array(z.string()).min(1).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Nothing to change")
export type UpdateStaffRequest = z.infer<typeof updateStaffSchema>

export const resetStaffPasswordSchema = z.object({ staffId: z.string() })
export type ResetStaffPassword = z.infer<typeof resetStaffPasswordSchema>

export const staffPasswordResetSchema = z.object({
  initialPassword: z.string(),
  sessionsClosed: z.number().int(),
})
export type StaffPasswordReset = z.infer<typeof staffPasswordResetSchema>

export const MIN_PASSWORD_LENGTH = 8

const password = z.string().min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`).max(200)

export const loginSchema = z.object({
  account: z.string().min(1, "Enter your account").max(200),
  /** No length check at sign-in: an older password may be shorter than the current floor, and rejecting it would lock the person out */
  password: z.string().min(1, "Enter your password").max(200),
})
export type LoginRequest = z.infer<typeof loginSchema>

export const updateProfileSchema = z
  .object({
    username: z.string().min(1, "Username cannot be empty").max(100).optional(),
    account: z.email("Account must be an email address").max(200).optional(),
  })
  .refine((b) => b.username !== undefined || b.account !== undefined, {
    message: "Nothing to update",
  })
export type UpdateProfile = z.infer<typeof updateProfileSchema>

/**
 * ⚠ **The old password must be verified**, even for someone already signed in — the session
 * could have been picked up from an unlocked machine.
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Enter your current password").max(200),
  newPassword: password,
})
export type ChangePassword = z.infer<typeof changePasswordSchema>

export const tenantSummarySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  environment: z.enum(ENVIRONMENTS),
  baseUrl: z.string(),
  colorSlot: z.number().int().min(1).max(8),
  active: z.boolean(),
  churned: z.boolean(),
  onboardedAt: z.string().nullable(),
  offboardedAt: z.string().nullable(),
  srvVersion: z.string().nullable(),
  detectedHost: z.string().nullable(),
  availableModules: z.array(z.string()).nullable(),
  identityProbedAt: z.iso.datetime({ offset: true }).nullable(),
  secretSetAt: z.iso.datetime({ offset: true }).nullable(),
  pollCron: z.string(),
  requestTimeoutMs: z.number().int(),
  notes: z.string().nullable(),
  state: z.enum(TILE_STATES),
  lastCollectedAt: z.iso.datetime({ offset: true }).nullable(),
  metricsLinked: z.number().int(),
  nextRunAt: z.iso.datetime({ offset: true }).nullable(),
  /**
   * ⚠ **Not the same thing as `state: "stale"`, and the difference decides who gets called.**
   * `stale` counts failed collections, so it means the customer's deployment is broken. This means
   * *we* did not even try — the collector is down, or never picked this customer up. The two are
   * blind to each other: a collector that never runs produces no failures at all, so every tile
   * keeps an `ok` badge over last week's numbers.
   */
  collectionOverdue: z.boolean(),
})
export type TenantSummary = z.infer<typeof tenantSummarySchema>

/**
 * ⚠ **`metricsTotal` is not optional detail.** The frontend once hand-wrote this as
 * `{ tenants: TenantSummary[] }` and dropped it — so the deployments table hard-coded
 * `{n}/90`. Add one metric to the catalog and that page keeps saying `/90`, **and never
 * errors**. The coverage matrix reads this field, so the two pages would quietly disagree
 * on the denominator.
 */
export const tenantListSchema = z.object({
  tenants: z.array(tenantSummarySchema),
  /** Total metrics in the catalog (currently 90). Use it when rendering "x/N wired" — never hard-code it */
  metricsTotal: z.number().int(),
})
export type TenantList = z.infer<typeof tenantListSchema>

export const createdTenantSchema = z.object({
  id: z.string(),
  colorSlot: z.number().int().min(1).max(8),
})
export type CreatedTenant = z.infer<typeof createdTenantSchema>

/**
 * ⚠ This was once a hard `min(32)` check, and that put the check **in the wrong place**:
 * `SHARED_SECRET` belongs to srv and existed before we did; we do not control its length.
 * Rejecting an existing 7-character key does not make that side stronger — it makes **that
 * deployment impossible to onboard**. A form built to onboard customers quickly would fail
 * on exactly the real, older deployments.
 */
export const RECOMMENDED_TENANT_SECRET_LENGTH = 32

const tenantSecret = z.string().min(1, "The key cannot be empty").max(512)

export const createTenantSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9-]{2,40}$/, "id must be lowercase letters, digits and hyphens"),
  displayName: z.string().min(1),
  environment: z.enum(ENVIRONMENTS),
  baseUrl: z.url().refine((u) => !u.endsWith("/"), "base_url must not end with a slash"),
  secret: tenantSecret.optional(),
  authSecretRef: z.string().min(1).optional(),
  notes: z.string().optional(),
}).refine((b) => Boolean(b.secret) !== Boolean(b.authSecretRef), {
  message: "Give exactly one of secret or authSecretRef",
  path: ["secret"],
})
export type CreateTenant = z.infer<typeof createTenantSchema>

export const updateTenantSchema = z.object({
  displayName: z.string().min(1).optional(),
  environment: z.enum(ENVIRONMENTS).optional(),
  baseUrl: z.url().refine((u) => !u.endsWith("/"), "base_url must not end with a slash").optional(),
  secret: tenantSecret.optional(),
  pollCron: z.string().min(1).optional(),
  requestTimeoutMs: z.number().int().min(1000).max(120_000).optional(),
  notes: z.string().nullable().optional(),
  active: z.boolean().optional(),
  offboardedAt: z.iso.date().nullable().optional(),
})
export type UpdateTenant = z.infer<typeof updateTenantSchema>

export const purgePreviewSchema = z.object({
  tenantId: z.string(),
  displayName: z.string(),
  facts: z.number().int(),
  metrics: z.number().int(),
  firstAt: z.iso.datetime({ offset: true }).nullable(),
  lastAt: z.iso.datetime({ offset: true }).nullable(),
  collectionTasks: z.number().int(),
  tilesReferencing: z.number().int(),
})
export type PurgePreview = z.infer<typeof purgePreviewSchema>

/**
 * ⚠ Checked on the **server**, not only in the dialog. A confirmation enforced only by the UI is not a
 * confirmation — it is a suggestion that anything holding the API can skip.
 */
export const purgeTenantSchema = z.object({ confirm: z.string().min(1) })
export type PurgeTenant = z.infer<typeof purgeTenantSchema>

export const purgeResultSchema = z.object({
  tenantId: z.string(),
  facts: z.number().int(),
  collectionTasks: z.number().int(),
  durationMs: z.number().int(),
})
export type PurgeResult = z.infer<typeof purgeResultSchema>

export const coverageMatrixSchema = z.object({
  tenants: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      environment: z.enum(ENVIRONMENTS),
      srvVersion: z.string().nullable(),
      colorSlot: z.number().int(),
    }),
  ),
  metrics: z.array(
    z.object({
      id: metricIdSchema,
      name: z.string(),
      domain: z.string(),
      priority: z.string(),
      cells: z.array(z.enum(TILE_STATES)),
    }),
  ),
  summary: z.object({
    metricsTotal: z.number().int(),
    metricsLinked: z.number().int(),
    metricsWithData: z.number().int(),
    metricsUnsupportedEverywhere: z.number().int(),
    acceptanceMet: z.boolean(),
  }),
})
export type CoverageMatrix = z.infer<typeof coverageMatrixSchema>

export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  requestId: z.string().optional(),
})
export type ApiErrorBody = z.infer<typeof apiErrorSchema>

/**
 * ⚠ **It is an array, and each entry succeeds or fails on its own** — not a
 * `Record<tileKey, TileResult>`. The route settles each request with `Promise.allSettled`:
 * if one tile on a screen queries across domains, the other eleven should not spin for
 * nothing, so the failed entry comes back carrying its own error envelope while the overall
 * response is still 200.
 */
export const tileQueryBatchItemSchema = z.discriminatedUnion("ok", [
  z.object({ tileKey: z.string(), ok: z.literal(true), result: tileResultSchema }),
  z.object({ tileKey: z.string(), ok: z.literal(false), status: z.number().int(), error: apiErrorSchema }),
])
export type TileQueryBatchItem = z.infer<typeof tileQueryBatchItemSchema>

export const tileQueryBatchResponseSchema = z.object({ results: z.array(tileQueryBatchItemSchema) })
export type TileQueryBatchResponse = z.infer<typeof tileQueryBatchResponseSchema>

export const WIDGET_TYPE_VALUES = WIDGET_TYPES
